import { AddInData, Geotab } from "@fairfleet/geotab";
import { useQuery } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import AddInIds from "@/constants/AddInIds";
import { RESULTS_LIMIT_ADD_IN_DATA } from "@/constants/resultsLimits";
import { decompressData } from "@/utilities/compression";
import { ManualRecordsCapError } from "@/utilities/error/ManualRecordsCapError";

export const DUTY_ROSTER_DATA_TYPE = "manualLogbookDutyRoster";
export const TRIP_END_DATA_TYPE = "manualLogbookTripEnd";
export const DUTY_ROSTERS_QUERY_KEY = "manualLogbookRecords";

/** One record per confirmed "manual logbook" popup in the EcoApp before departure (spec 4.3). */
export interface DutyRosterRecord {
  /** Id of the AddInData entity. */
  id: string;
  userId: string;
  deviceId: string;
  dutyRosterNumber: string;
  /** Digits only, empty when unknown. */
  odometerKm: string;
  recordedAt: Date;
}

/**
 * One record per end-of-trip popup of a manual logbook trip in the EcoApp (the odometer block
 * of that popup). Written while the trip is still running, so `recordedAt` lies before Geotab's
 * trip stop.
 */
export interface TripEndRecord {
  id: string;
  userId: string;
  deviceId: string;
  /** Roster number from the start popup of the same journey; empty when the app was restarted in between. */
  dutyRosterNumber: string;
  /** Digits only, empty when the driver left it blank. */
  odometerKm: string;
  recordedAt: Date;
}

export interface ManualLogbookRecords {
  dutyRosters: DutyRosterRecord[];
  tripEnds: TripEndRecord[];
}

interface RawDetails {
  dataType?: string;
  userId?: string;
  deviceId?: string;
  dutyRosterNumber?: string;
  odometerKm?: string;
  recordedAt?: string;
}

/**
 * Loads the records the EcoApp writes for manual logbook trips: duty roster confirmations with
 * `recordedAt` in `[fromDate − lead, toDate]` and trip-end odometer entries in
 * `[fromDate − lead, toDate + tail]`. Geotab's AddInData `whereClause` accepts exactly one condition
 * without a `details.` prefix, so the lower bound is the server-side condition and the record
 * kind is filtered here. The lead covers a roster confirmed before the period for a trip inside
 * it; the tail covers the end dialog the driver fills in after the period's last trip stopped.
 */
export function useManualLogbookRecords(
  fromDate: Date,
  toDate: Date,
  leadMs: number,
  tailMs: number,
  enabled = true,
) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  const since = new Date(fromDate.getTime() - leadMs);
  const until = new Date(toDate.getTime() + tailMs);

  return useQuery<ManualLogbookRecords>({
    queryKey: [DUTY_ROSTERS_QUERY_KEY, activeSessionId, since, toDate, until],
    enabled,
    // A full page is deterministic; retrying would only download the same records again.
    retry: (failureCount, error) => !(error instanceof ManualRecordsCapError) && failureCount < 3,
    queryFn: async ({ signal }) => {
      const records = await fetchRecordsSince(geotabClient, since, signal);
      const result: ManualLogbookRecords = { dutyRosters: [], tripEnds: [] };
      for (const record of records) {
        const details = decompressData<RawDetails>(record.details);
        if (!details?.deviceId || !details.recordedAt) continue;
        const recordedAt = new Date(details.recordedAt);
        if (Number.isNaN(recordedAt.getTime())) continue;
        const base = { id: record.id, userId: details.userId ?? "", deviceId: details.deviceId, odometerKm: details.odometerKm ?? "", recordedAt };
        const withNumber = { ...base, dutyRosterNumber: details.dutyRosterNumber ?? "" };
        if (details.dataType === DUTY_ROSTER_DATA_TYPE && recordedAt <= toDate) {
          result.dutyRosters.push(withNumber);
        } else if (details.dataType === TRIP_END_DATA_TYPE && recordedAt <= until) {
          result.tripEnds.push(withNumber);
        }
      }
      return result;
    },
  });
}

/**
 * Every AddInData record with `recordedAt > since`. The whereClause has only the lower bound, so
 * a long look-back can fill a page; a full page throws {@link ManualRecordsCapError} instead of
 * continuing, because nothing documents how Geotab orders AddInData and a continuation could skip
 * bookings that were written late. Until the ordering (or an upper bound in the whereClause) is
 * confirmed, failing is the honest outcome.
 */
export async function fetchRecordsSince(
  geotabClient: Geotab,
  since: Date,
  signal?: AbortSignal,
): Promise<AddInData[]> {
  const page = (await geotabClient.get(
    "AddInData",
    { addInId: AddInIds.geotabAddin, whereClause: `recordedAt > "${since.toISOString()}"` },
    RESULTS_LIMIT_ADD_IN_DATA,
    signal,
  )) as AddInData[];
  if (page.length >= RESULTS_LIMIT_ADD_IN_DATA) throw new ManualRecordsCapError(RESULTS_LIMIT_ADD_IN_DATA);
  return page;
}
